/**
 * The transcript's sticky outline sidebar: the expanded panel (header, close
 * button, outline tree) or the collapsed show-outline toggle, pinned below
 * the swimlanes via StickyScroll.
 */

import clsx from "clsx";
import {
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useMemo,
  useState,
} from "react";

import { StickyScroll } from "@tsmono/react/components";
import { Icon } from "@tsmono/react/icons";

import { TranscriptOutline } from "./outline/TranscriptOutline";
import {
  outlineCollapseState,
  type OutlineCollapseState,
} from "./outline/useOutlineCollapse";
import styles from "./OutlineSidebar.module.css";
import { EventNode, type TranscriptCollapseState } from "./types";

export interface TranscriptLayoutOutlineProps {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  toggleDisabled?: boolean;
  toggleTitle?: string;
  /** Header title shown next to the toggle icon when expanded. */
  title?: string;
  /** Name of the agent/subagent currently displayed. Shown as a header in the outline. */
  name?: string;
  renderLink?: (url: string, children: ReactNode) => ReactNode;
  onNavigateToEvent?: (eventId: string) => void;
  selectedId?: string | null;
  setSelectedId?: (id: string) => void;
  /** Optional ref to the outline's sticky scroll container. Useful when the
   *  caller wants to observe its scroll events (e.g. headroom direction). */
  scrollRef?: RefObject<HTMLDivElement | null>;
}

export interface OutlineSidebarProps {
  outline: TranscriptLayoutOutlineProps;
  /** Effective collapsed state (user intent + auto-hide). */
  isCollapsed: boolean;
  /** Whether the outline has displayable nodes (gates the show toggle). */
  hasNodes: boolean;
  onHasNodesChange: (hasNodes: boolean) => void;
  eventNodes: EventNode[];
  defaultCollapsedIds: Record<string, boolean>;
  /** The main scroll container. */
  scrollRef: RefObject<HTMLDivElement | null>;
  /** Transcript identity scoping the outline's persisted scroll state. */
  listId?: string;
  running: boolean;
  backfilling: boolean;
  /** Resolved agent name header (outline.name or the selected row). */
  agentName?: string;
  /** Sticky offset below the swimlanes. */
  offsetTop: number;
  collapseState?: TranscriptCollapseState;
  getEventUrl?: (eventId: string) => string | undefined;
}

export const OutlineSidebar: FC<OutlineSidebarProps> = ({
  outline,
  isCollapsed,
  hasNodes,
  onHasNodesChange,
  eventNodes,
  defaultCollapsedIds,
  scrollRef,
  listId,
  running,
  backfilling,
  agentName,
  offsetTop,
  collapseState,
  getEventUrl,
}) => {
  // Capture the outline's own scroll container (the StickyScroll div, which
  // has overflow-y:auto) into state so the outline's virtual list can use it
  // as its scroll parent. Resolving into state (rather than reading a ref
  // during render) guarantees a re-render once the element mounts. Also
  // mirror it into the optional external ref callers pass for wheel
  // forwarding.
  const [outlineScrollEl, setOutlineScrollEl] = useState<HTMLDivElement | null>(
    null
  );
  const { scrollRef: externalScrollRef } = outline;
  const handleOutlineScrollRef = useCallback(
    (el: HTMLDivElement | null) => {
      setOutlineScrollEl(el);
      if (externalScrollRef) {
        externalScrollRef.current = el;
      }
    },
    [externalScrollRef]
  );

  const collapse: OutlineCollapseState | undefined = useMemo(
    () => outlineCollapseState(collapseState),
    [collapseState]
  );

  return (
    <>
      <StickyScroll
        ref={handleOutlineScrollRef}
        scrollRef={scrollRef}
        className={clsx(styles.outline, !isCollapsed && styles.expanded)}
        offsetTop={offsetTop}
      >
        {!isCollapsed ? (
          <>
            {outline.title && (
              <div className={styles.sidebarHeader}>
                <span
                  className={clsx(
                    styles.sidebarHeaderTitle,
                    "text-size-smaller"
                  )}
                >
                  {outline.title}
                </span>
              </div>
            )}
            <div className={styles.sidebarHeaderCloseAnchor}>
              <button
                type="button"
                className={styles.sidebarHeaderClose}
                onClick={() => outline.onCollapsedChange(true)}
                aria-label="Hide outline"
                title={outline.toggleTitle ?? "Hide outline"}
              >
                <Icon name="close" />
              </button>
            </div>
            <TranscriptOutline
              eventNodes={eventNodes}
              defaultCollapsedIds={defaultCollapsedIds}
              scrollRef={scrollRef}
              outlineScrollEl={outlineScrollEl}
              listId={listId}
              running={running}
              backfilling={backfilling}
              agentName={agentName}
              scrollTrackOffset={offsetTop}
              collapse={collapse}
              selectedOutlineId={outline.selectedId}
              setSelectedOutlineId={outline.setSelectedId}
              getEventUrl={getEventUrl}
              renderLink={outline.renderLink}
              onNavigateToEvent={outline.onNavigateToEvent}
              onHasNodesChange={onHasNodesChange}
            />
          </>
        ) : (
          <button
            type="button"
            className={styles.outlineToggle}
            onClick={
              hasNodes && !outline.toggleDisabled
                ? () => outline.onCollapsedChange(false)
                : undefined
            }
            aria-disabled={outline.toggleDisabled || !hasNodes}
            title={
              outline.toggleTitle ??
              (!hasNodes
                ? "No outline available for the current filter"
                : undefined)
            }
            aria-label="Show outline"
          >
            <Icon name="sidebar" />
          </button>
        )}
      </StickyScroll>
      <div className={styles.separator} />
    </>
  );
};
